//
// File: sentry.ts
// Description: Initializes Sentry error monitoring and Session Replay for the
// shell and every micro frontend it hosts. One SDK instance runs per page.
// Copyright: Broadlume 2024
//

import {
  captureException,
  getClient,
  init,
  makeFetchTransport,
  makeMultiplexedTransport,
  moduleMetadataIntegration,
  MULTIPLEXED_TRANSPORT_EXTRA_KEY,
  openFeatureIntegration,
  replayIntegration,
} from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import type { ErrorInfo } from 'react';

type ModuleMetadata = { dsn?: string };

/**
 * Sends an event to the Sentry project of the code that threw it.
 *
 * Every build — the shell's and each micro frontend's — tags the files it
 * emits with its own DSN, via @sentry/webpack-plugin module metadata. So the
 * frame that threw names its owner.
 *
 * Attribution uses that frame alone, never a caller further down the stack.
 * A plugin that has not set up Sentry is almost always called by the shell,
 * so trusting a lower frame would file the plugin's bugs against the shell.
 * When the throwing code claims no project — an unconfigured plugin, a
 * browser extension, a third-party script — the event is dropped.
 * https://docs.sentry.io/platforms/javascript/best-practices/micro-frontends/
 */
export const routeEventToOwnerProject = (event: ErrorEvent): ErrorEvent | null => {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames?.length) {
    // Nothing to attribute — a captureMessage call, or a cross-origin
    // "Script error" the browser gave us no stack for. Keep it here.
    return event;
  }

  // Frames run oldest first, so the throwing code is at the end. Frames with
  // no filename are native or anonymous; they name no owner, so skip them.
  const throwingFrame = frames.filter((frame) => frame.filename).pop();
  const owner: ModuleMetadata | undefined = throwingFrame?.module_metadata;

  if (!owner?.dsn) {
    return null;
  }

  event.extra = { ...event.extra, [MULTIPLEXED_TRANSPORT_EXTRA_KEY]: [owner] };
  return event;
};

/**
 * Reports a plugin that a ShellComponent failed to load or caught crashing.
 * React does not rethrow errors that a boundary catches, so nothing else
 * sees render crashes.
 *
 * Uses captureException, not captureReactException. The latter links a
 * synthetic component-stack error as the cause, which becomes
 * `exception.values[0]`; routeEventToOwnerProject would then attribute the
 * event by component-stack frames instead of the frame that threw.
 */
export const reportPluginError = (error: Error, errorInfo?: ErrorInfo): void => {
  captureException(
    error,
    errorInfo?.componentStack
      ? { contexts: { react: { componentStack: errorInfo.componentStack } } }
      : undefined,
  );
};

/**
 * Any host on cyncly.com, at any subdomain depth, over http or https.
 *
 * The dot is required before `cyncly.com`, so a lookalike host such as
 * `evilcyncly.com` does not match. An exact `cyncly.com` still matches.
 */
export const CYNCLY_URL_PATTERN = /^https?:\/\/(?:[^/?#]+\.)?cyncly\.com(?::\d+)?(?:[/?#]|$)/;

/**
 * Headers worth seeing in a replay. The SDK has no wildcard: it matches this
 * list exactly, lowercased, and always adds `content-type`, `content-length`,
 * and `accept`.
 *
 * `authorization` and `cookie` are deliberately absent. Adding either would
 * ship a live bearer token or session cookie to Sentry.
 */
export const CAPTURED_NETWORK_HEADERS = [
  'accept-language',
  'cache-control',
  'content-encoding',
  'etag',
  'if-none-match',
  'location',
  'referrer-policy',
  'retry-after',
  'user-agent',
  'x-amzn-requestid',
  'x-amzn-trace-id',
  'x-request-id',
];

/**
 * No-op when no DSN is configured, or when a micro frontend already
 * initialized the shared SDK. Sentry forbids a second init or Replay instance.
 */
export const initSentry = (): void => {
  const dsn = process.env.NX_PUBLIC_SENTRY_DSN;
  if (!dsn || getClient()) {
    return;
  }
  const environment = process.env.NX_PUBLIC_SENTRY_ENVIRONMENT || 'local';
  init({
    dsn,
    environment: environment,
    integrations: [
      moduleMetadataIntegration(),
      openFeatureIntegration(),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Mask PII from what user types
        maskAllInputs: true,
        // Opt-in escape hatch: put `data-sentry-mask` (or class `sentry-mask`) on
        // any element that renders customer PII as plain text.
        mask: ['.sentry-mask', '[data-sentry-mask]', '.pii-mask', '[data-pii-mask]'],
        // Capture URL, method, status, headers, and bodies for our own APIs
        // only. Third-party calls keep the default summary-level detail.
        networkDetailAllowUrls: [CYNCLY_URL_PATTERN],
        networkCaptureBodies: true,
        networkRequestHeaders: CAPTURED_NETWORK_HEADERS,
        networkResponseHeaders: CAPTURED_NETWORK_HEADERS,
      }),
    ],
    transport: makeMultiplexedTransport(makeFetchTransport),
    replaysSessionSampleRate: environment !== 'prod' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: routeEventToOwnerProject,
  });
};
